import React from 'react';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import MarketInsightService from '@app/model/market_insight/service/MarketInsightService';
import { MarketInsightQueryKeys } from '@app/model/market_insight/service/MarketInsightQueryKeys';
import { useQuery } from '@tanstack/react-query';
import Portlet from '@app/component/portlet/Portlet';
import _isEmpty from 'lodash/isEmpty';
import Utilities from '@util/Utilities';
import MarketInsightOccupancyRatingIndex from '@app/model/market_insight/MarketInsightOccupancyRatingIndex';
import Rating from '@app/component/rating/Rating';
import HotelRatingType from '@app/model/hotel/HotelRatingType';
import MarketInsightOccupancy from '@app/model/market_insight/MarketInsightOccupancy';
import MarketInsightOccupancyDrillDownType from '@app/model/market_insight/MarketInsightOccupancyDrillDownType';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import MarketInsightOccupancyRoomTypeIndex from '@app/model/market_insight/MarketInsightOccupancyRoomTypeIndex';

// marketAreaId: the market of the market tab; null = the market of the hotel.
const MarketInsightOccupancyIndicesFragment: React.FunctionComponent<{ statDate: string, marketAreaId: number | null }> = ({ statDate, marketAreaId }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { data: occupancyIndices, isLoading, isSuccess } = useQuery({
		queryFn: () => MarketInsightService.findOccupancyIndices({ companyHotelId: companyHotel.id, statDate: statDate, marketAreaId: marketAreaId }),
		queryKey: MarketInsightQueryKeys.occupancyIndices(companyHotel.id, statDate, marketAreaId),
	});

	if (isLoading) {
		return <Portlet title="Occupancy">
			<div className="animate-pulse flex items-center gap-x-2">
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5" />
				<div className="h-2.5 bg-gray-200 rounded-full w-full mb-2.5" />
			</div>
		</Portlet>;
	}
	if (isSuccess && occupancyIndices !== undefined && occupancyIndices !== null) {

		return <Portlet title="Occupancy">
			<div className="flex flex-wrap items-center gap-x-2">
				<div className="grow">
					<div className="flex items-center gap-x-2">
						{occupancyIndices.marketOccupancyRate == null ?
							<p className="text-2xl font-semibold text-gray-400">n/a</p> :
							<p className="text-2xl font-semibold text-gray-900">{Utilities.formatDouble(occupancyIndices.marketOccupancyRate * 100, 0)}%</p>}
					</div>
					<p className="block text-xs font-light text-gray-400">Market</p>
				</div>
				<div className="mx-3 h-6 w-px bg-gray-100 block"></div>
				<div className="grow">
					<div className="flex items-center gap-x-2">
						{occupancyIndices.myPropertyOccupancyRate == null ?
							<p className="text-2xl font-semibold text-gray-400">n/a</p> :
							<p className="text-2xl font-semibold text-gray-900">{Utilities.formatDouble(occupancyIndices.myPropertyOccupancyRate * 100, 0)}%</p>}
					</div>
					<p className="block text-xs font-light text-gray-400">My property</p>
				</div>
			</div>
			<DisplayOccupancyDrillDown occupancyIndices={occupancyIndices}/>
		</Portlet>;

	}

	return <Portlet title="Occupancy">
		<p className="block text-xs font-light text-gray-400">Not Available</p>
	</Portlet>;
};

const DisplayOccupancyDrillDown: React.FunctionComponent<{occupancyIndices: MarketInsightOccupancy}> = ({occupancyIndices}) => {
	if (occupancyIndices.occupancyDrillDownType === MarketInsightOccupancyDrillDownType.HOTEL_RATING) {
		const sortedIndices = !_isEmpty(occupancyIndices.ratingIndices) ? [...occupancyIndices.ratingIndices].sort((a, b) => a.rating - b.rating) : [];
		return <div className="mt-2">
			<div className="grid grid-cols-1 bg-white sm:grid-cols-2 lg:grid-cols-3">
				{sortedIndices.filter(occupancyIndex => occupancyIndex.rating > 2)
					.map((occupancyIndex, index) => <DisplayOccupancyRatingIndex key={occupancyIndex.rating} occupancyIndex={occupancyIndex} index={index}/>)}
			</div>
		</div>
	} else if (occupancyIndices.occupancyDrillDownType === MarketInsightOccupancyDrillDownType.ROOM_TYPE) {
		// Only Standard, Premium and Suite get a bar, in the enum order. Unclassified rooms (NONE) count in the headline only.
		const sortOrder = Object.values(HotelRoomMappingType);
		const sortedIndices = !_isEmpty(occupancyIndices.roomTypeIndices) ? occupancyIndices.roomTypeIndices
			.filter(occupancyIndex => occupancyIndex.roomType !== HotelRoomMappingType.NONE)
			.sort((a, b) => sortOrder.indexOf(a.roomType) - sortOrder.indexOf(b.roomType)) : [];

		return <div className="mt-2">
			<div className="grid grid-cols-1 bg-white sm:grid-cols-3">
				{sortedIndices.map((occupancyIndex, index) => <DisplayOccupancyRoomTypeIndex key={occupancyIndex.roomType} occupancyIndex={occupancyIndex} index={index}/>)}
			</div>
		</div>
	}

	return <div/>
}

const DisplayOccupancyRatingIndex: React.FunctionComponent<{ occupancyIndex: MarketInsightOccupancyRatingIndex, index: number }> = ({ occupancyIndex, index }) => {
	const marketPercentage = Utilities.formatDouble(occupancyIndex.marketRate * 100, 0);
	return <div className={`border-t border-gray-100 px-2 py-3 ${index > 0 ? 'sm:border-l' : ''}`}>
		<p className="text-sm/6 font-medium text-gray-400"><Rating rating={occupancyIndex.rating} ratingType={HotelRatingType.STARS}/></p>
		<div className="mt-2">
			<div className="text-xs font-medium text-gray-900">{marketPercentage}%<span
				className="ml-1 text-[7px] font-light text-gray-400">Market Oc.</span></div>
			<div className="overflow-hidden rounded-full w-full bg-gray-100">
				<div className={`h-2 rounded-full ${findBackgroundColor(occupancyIndex.marketRate * 100)}`} style={{width: `${occupancyIndex.marketRate * 100}%`}}></div>
			</div>
		</div>

	</div>;
};


const DisplayOccupancyRoomTypeIndex: React.FunctionComponent<{ occupancyIndex: MarketInsightOccupancyRoomTypeIndex, index: number }> = ({ occupancyIndex, index }) => {
	const marketPercentage = Utilities.formatDouble(occupancyIndex.marketRate * 100, 0);
	return <div className={`border-t border-gray-100 px-2 py-3 ${index > 0 ? 'sm:border-l' : ''}`}>
		<p className="text-sm/6 font-medium text-gray-400">{Utilities.snakeCaseToWords(occupancyIndex.roomType)}</p>
		<div className="mt-2">
			<div className="text-xs font-medium text-gray-900">{marketPercentage}%<span
				className="ml-1 text-[9px] font-light text-gray-400">Market</span></div>
			<div className="overflow-hidden rounded-full w-full bg-gray-100">
				<div className={`h-2 rounded-full ${findBackgroundColor(occupancyIndex.marketRate * 100)}`} style={{width: `${occupancyIndex.marketRate * 100}%`}}></div>
			</div>
		</div>

	</div>;
}

const findBackgroundColor = (rating: number) => {
	if(rating < 20) return "bg-green-500";
	if(rating < 40 && rating >= 20) return "bg-amber-500";
	if(rating < 60 && rating >= 40) return "bg-pink-500";
	if(rating < 80 && rating >= 60) return "bg-red-500";
	return "bg-green-500";
}


export default MarketInsightOccupancyIndicesFragment;